import React, { useState, useRef, useEffect } from 'react';
import Edit from 'react-icons/lib/md/edit';
import { defaultMessages } from '../../../libs/i18n/default';
import Trash from 'react-icons/lib/fa/trash';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import FormControl from 'react-bootstrap/lib/FormControl';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import { CustomFormGroup } from '../../../shared/components';
import { pageValid, fieldsToValidate } from '../../../shared/validator';
import { changeState } from '../../../shared/utilities';
import { addPhoto, destroyPhoto } from '../actions';
import withDefaults from './withDefaults'
import { get, capitalize } from 'lodash'

const UploadPhoto = ({ photo: existingPhoto, dispatch, intl: { formatMessage } }) => {
  const [showModal, setShowModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const disabledStyle = { pointerEvents: 'none', opacity: 0.6 }
  const kindSelections = ['personal', 'family', 'other'].map(opt => (
    <option key={opt} value={opt}>{capitalize(opt)}</option>
  ))
  const [photo, setPhoto] = useState({
    id: get(existingPhoto, 'id', ''),
    name: {
      val: get(existingPhoto, 'name', ''),
      validationKey: 'photo_name',
      profileElement: true
    },
    kind: {
      val: get(existingPhoto, 'kind'),
      validationKey: 'photo_kind',
      profileElement: true
    },
    description: {
      val: get(existingPhoto, 'description', ''),
      validationKey: 'photo_description',
      profileElement: true
    },
    file: {
      val: null,
      validationKey: !get(existingPhoto, 'id') && 'photo_file',
      profileElement: true
    },
    url: get(existingPhoto, 'url'),
  })
  const photoRef = useRef(photo)
  photoRef.current = photo
  const isValidPhotoFormat = photo.file.val && (photo.file.val.type === 'image/png' || photo.file.val.type === 'image/jpeg')

  useEffect(() => {
    fieldsToValidate(photo).forEach(f => {
      handleChange(f, photo[f].val, false)
    })
  }, [])

  const handleChange = (key, val, resetDependentObject = true) => {
    changeState(
      photoRef.current,
      key,
      val,
      (newObj) => {
        setPhoto(prevPhoto => ({...prevPhoto, ...newObj}))
      },
      resetDependentObject
    )
  }

  const handleEditPhoto = (e) => {
    e.preventDefault()
    setShowModal(true)
  }

  const resetPhoto = () => {
    setPhoto({
      ...photo,
      name: { ...photo.name, val: '' },
      kind: { ...photo.kind, val: '' },
      description: { ...photo.description, val: '' },
      file: { ...photo.file, val: null },
    })
  }

  const handleAddPhoto = (e) => {
    e.preventDefault()
    setSubmitting(true)
    dispatch(addPhoto({
      id: photo.id,
      name: photo.name.val,
      kind: photo.kind.val,
      description: photo.description.val,
      file: photo.file.val
    }))
    .then(() => {
      if (!photo.id){
        resetPhoto()
      }
      setShowModal(false)
      setSubmitting(false)
    })
  }

  const handleDestroyPhoto = (e) => {
    e.preventDefault()
    setDeleting(true)
    dispatch(destroyPhoto(photo.id))
  }

  const loadingIcon = <i className='fa fa-spinner fa-spin fa-lg fa-fw'></i>

  const buttonTitle = () => {
    if (submitting) return (defaultMessages.clientHostPagesPhotoButtonsUploading)
    return photo.id ? defaultMessages.clientHostPagesPhotoButtonsUpdate : defaultMessages.clientHostPagesPhotoButtonsCreate
  }

  const loadingAnimation = () => (
    submitting && loadingIcon
  )

  const displayPhoto = () => (
    photo.id
    ? <div className='guest-photo-icon'>
        <div style={{...(deleting ? disabledStyle : {}), display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
          <ControlLabel>{`${existingPhoto.title}`}</ControlLabel>
          <br />
          <img src={photo.url} />
          <div style={{width: '100%', bottom: '5px'}}>
            {
              deleting
              ? loadingIcon
              : <>
                  <a href="#" onClick={e => handleEditPhoto(e)}><Edit/></a>
                  <a href="#" onClick={e => handleDestroyPhoto(e)}><Trash/></a>
                </>
            }
          </div>
        </div>
      </div>
    : <Button
        style={{marginBottom: 20}}
        type='button'
        className='btn-default btn-fixed-width'
        onClick={e => handleEditPhoto(e)}>
        Add Photo
      </Button>
  )

  const submitEnabled = () => (
    !submitting && (photo.id || isValidPhotoFormat) && pageValid(photo)
  )

  const modal = () => (
    <Modal id='guest-photo-modal' show={showModal} onHide={() => setShowModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>{photo.id ? 'Edit Photo' : 'New Photo'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <form>
            {
              photo.url &&
              <img style={{maxWidth: '80%', marginBottom: 10}} src={photo.url} className="img-polaroid"/>
            }

            <CustomFormGroup field={photo.name}>
              <ControlLabel htmlFor="name">{formatMessage(defaultMessages.clientGuestPagesPersonalityProfileLabelsPhotoTitle)}</ControlLabel>
              <FormControl
                id="name"
                type="text"
                componentClass="input"
                value={photo.name.val}
                onChange={(e) => handleChange(e.target.id, e.target.value)}/>
            </CustomFormGroup>

            <CustomFormGroup field={photo.description}>
              <ControlLabel htmlFor="description">{formatMessage(defaultMessages.clientGuestPagesPersonalityProfileLabelsPhotoDescription)}</ControlLabel>
              <FormControl
                id="description"
                type="text"
                componentClass="input"
                value={photo.description.val}
                onChange={(e) => handleChange(e.target.id, e.target.value)}/>
            </CustomFormGroup>

            <CustomFormGroup field={photo.kind}>
              <ControlLabel htmlFor="kind">{formatMessage(defaultMessages.clientGuestPagesPersonalityProfileLabelsPhotoKind)}</ControlLabel>
              <FormControl
                id="kind"
                type="text"
                defaultValue={photo.kind.val}
                componentClass="select"
                onChange={(e) => handleChange('kind', e.target.value)}>
                <option key='no_select' value="">{formatMessage(defaultMessages.clientCommonSelectsSelect)}</option>
                {kindSelections}
              </FormControl>
            </CustomFormGroup>

            {
              !photo.id &&
              <CustomFormGroup
                field={{
                  ...photo.file,
                  valid: photo.file.valid && isValidPhotoFormat
                }}
              >
                <ControlLabel htmlFor="image">{formatMessage(defaultMessages.clientGuestPagesPersonalityProfileLabelsPhotoFile)}</ControlLabel>
                <p>
                  <small>{formatMessage(defaultMessages.clientCommonInvalidPhotoFormat)}</small>
                </p>
                <FormControl
                  id="image"
                  type="file"
                  accept="image/png, image/jpeg"
                  componentClass="input"
                  onChange={(e) => handleChange('file', e.target.files[0])}
                />
              </CustomFormGroup>
            }

            <Button id="photo-submit" className="btn-primary" onClick={e => handleAddPhoto(e)} disabled={!submitEnabled()}>
              {loadingAnimation()} {formatMessage(buttonTitle())}
            </Button>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  )
  
  return (
    <>
      {modal()}
      {displayPhoto()}
    </>
  )
}

export default withDefaults(UploadPhoto)
